// ─────────────────────────────────────────────────────────────────────────────
// Helpers statiques pour la gestion des popups de filtres (date et nombre)
// ─────────────────────────────────────────────────────────────────────────────

// ─────────────────────────────────────────────────────────────────────────────
// Registry & cleanup des popups
// ─────────────────────────────────────────────────────────────────────────────
/** Singleton : ferme le popup de filtre actuellement ouvert (date ou nombre) */
let activeClosePopup: (() => void) | null = null;

interface PopupEntry {
	el: HTMLElement;
	outsideClickHandler: (e: MouseEvent) => void;
}
const _popupRegistry: PopupEntry[] = [];

export function destroyAllFilterPopups(): void {
	_popupRegistry.forEach(({ el, outsideClickHandler }) => {
		el.parentNode?.removeChild(el);
		document.removeEventListener('click', outsideClickHandler);
	});
	_popupRegistry.length = 0;
	activeClosePopup = null;
}

// ─────────────────────────────────────────────────────────────────────────────
// Primitives internes partagées
// ─────────────────────────────────────────────────────────────────────────────

const POPUP_STYLE =
	'display:none; position:fixed; z-index:10000; background:white; padding:10px; border:1px solid #ccc; border-radius:5px; box-shadow:0 5px 15px rgba(0,0,0,.3); width:280px; box-sizing:border-box;';

/** Crée le bouton déclencheur avec icône + placeholder texte */
function createFilterTriggerBtn(iconClass: string): { btn: HTMLButtonElement; placeholder: HTMLSpanElement } {
	const btn = document.createElement('button');
	const placeholder = document.createElement('span');
	placeholder.innerText = 'Filtrer...';
	const icon = document.createElement('i');
	icon.className = iconClass;
	icon.style.marginRight = '4px';
	btn.appendChild(icon);
	btn.appendChild(placeholder);
	return { btn, placeholder };
}

/** Crée et enregistre un popup rattaché au body */
function createFilterPopup(triggerBtn: HTMLButtonElement): {
	popup: HTMLDivElement;
	openPopup: () => void;
	closePopup: () => void;
} {
	const popup = document.createElement('div');
	popup.style.cssText = POPUP_STYLE;
	document.body.appendChild(popup);

	function positionPopup() {
		const rect = triggerBtn.getBoundingClientRect();
		popup.style.top = `${rect.bottom + window.scrollY}px`;
		popup.style.left = `${rect.left + window.scrollX}px`;
	}

	function closePopup() {
		popup.style.display = 'none';
		if (activeClosePopup === closePopup) activeClosePopup = null;
	}

	function openPopup() {
		positionPopup();
		popup.style.display = 'block';
		activeClosePopup = closePopup;
	}

	const outsideClickHandler = (e: MouseEvent) => {
		if (!triggerBtn.contains(e.target as Node) && !popup.contains(e.target as Node)) {
			closePopup();
		}
	};
	document.addEventListener('click', outsideClickHandler);
	_popupRegistry.push({ el: popup, outsideClickHandler });

	return { popup, openPopup, closePopup };
}

// ─────────────────────────────────────────────────────────────────────────────
// Factory commune : toute la logique partagée entre date et nombre
// ─────────────────────────────────────────────────────────────────────────────

interface PopupFilterOptions {
	/** Icône du bouton déclencheur */
	triggerIconClass: string;
	/** Reçoit le popup vide et le remplit, retourne une fn updatePlaceholder */
	renderContent: (popup: HTMLDivElement, close: () => void, updatePlaceholder: (text: string) => void) => void;
}

/**
 * Factory interne partagée par makeDateRangeFilter et makeNumberRangeFilter.
 * Gère : création du container, bouton déclencheur, popup body, positionnement,
 * activeClosePopup, listener outside-click.
 *
 * Le paramètre `renderContent` injecte le contenu spécifique (date ou nombre)
 * et branche les callbacks apply/clear.
 */
function createPopupFilter(options: PopupFilterOptions): HTMLDivElement {
	const container = document.createElement('div');
	container.style.cssText = 'position:relative; display:flex; align-items:center; width:100%';

	const { btn: triggerBtn, placeholder } = createFilterTriggerBtn(options.triggerIconClass);
	container.appendChild(triggerBtn);

	const { popup, openPopup, closePopup } = createFilterPopup(triggerBtn);

	// Callback exposé au renderContent pour mettre à jour le texte du bouton
	function updatePlaceholder(text: string) {
		placeholder.innerText = text || 'Filtrer...';
	}

	// Délégation du contenu spécifique au filtre
	options.renderContent(popup, closePopup, updatePlaceholder);

	// Gestion du toggle du popup
	triggerBtn.addEventListener('click', (e) => {
		e.stopPropagation();
		const willOpen = popup.style.display === 'none';
		if (activeClosePopup) {
			activeClosePopup();
			activeClosePopup = null;
		}
		if (willOpen) openPopup();
	});

	return container;
}

// ─────────────────────────────────────────────────────────────────────────────
// makeDateRangeFilter — contenu spécifique uniquement
// ─────────────────────────────────────────────────────────────────────────────

export function makeDateRangeFilter(
	_cell: unknown,
	_onRendered: (cb: () => void) => void,
	success: (val: unknown) => void,
	_cancel: unknown,
	headerParams: { compile?: ng.ICompileService; parentScope?: ng.IScope; globalizationManagementService?: any; moment?: any }
) {
	const $compile = headerParams?.compile;
	const $scope = headerParams?.parentScope;
	const globalizationManagementService = headerParams?.globalizationManagementService;
	const moment = headerParams?.moment;

	if (!$compile || !$scope) {
		console.error('makeDateRangeFilter requires headerFilterParams: { compile: $compile, parentScope: $scope }');
		return document.createElement('div');
	}

	let dateFormat = 'DD/MM/YYYY';
	if (globalizationManagementService && typeof globalizationManagementService.getCurrentLanguage === 'function') {
		const lang = globalizationManagementService.getCurrentLanguage();
		if (lang && lang.dateFormat) {
			dateFormat = lang.dateFormat.toUpperCase(); // Ensure moment format matches (e.g., dd/MM/yyyy -> DD/MM/YYYY)
		}
	}

	return createPopupFilter({
		triggerIconClass: 'fa fa-calendar',
		renderContent(popup, closePopup, updatePlaceholder) {
			const filterScope = $scope.$new(true) as ng.IScope & {
				filter: { start: unknown; end: unknown };
				applyFilter: (e: MouseEvent) => void;
				clearFilter: (e: MouseEvent) => void;
			};
			filterScope.filter = { start: null, end: null };

			// Restore initial values from the filter cell if available
			const initialValue = (_cell as { getValue?: () => unknown })?.getValue?.() as Array<{ type: string; value: unknown }> | undefined;
			if (Array.isArray(initialValue)) {
				const geFilter = initialValue.find((f) => f.type === 'Ge');
				const ltFilter = initialValue.find((f) => f.type === 'Lt');
				if (geFilter && geFilter.value) {
					filterScope.filter.start = geFilter.value; // It is likely already a 'DD/MM/YYYY' string
				}
				if (ltFilter && ltFilter.value) {
					filterScope.filter.end = ltFilter.value;
				}
			}

			const inner = $compile(`
                <div style="width:100%; box-sizing:border-box;">
                    <div style="margin-bottom:5px;"><strong>Du</strong></div>
                    <div ac-datepicker ng-model="filter.start" ac-datepicker-validation-strict="false" style="margin-bottom:15px;"></div>
                    <div style="margin-bottom:5px;"><strong>Au</strong></div>
                    <div ac-datepicker ng-model="filter.end" ac-datepicker-validation-after="filter.start" ac-datepicker-validation-strict="false" style="margin-bottom:15px;"></div>
                    <div style="display:flex; gap:5px;">
                        <button class="btn btn-default btn-sm" style="flex:1;" ng-click="applyFilter($event)">Filtrer</button>
                        <button class="btn btn-danger btn-sm" title="Effacer" ng-click="clearFilter($event)"><i class="fa fa-times"></i></button>
                    </div>
                </div>
            `)(filterScope);
			popup.appendChild(inner[0]);

			function formatDisplay(val: unknown): string | null {
				if (!val) return null;
				if (moment && moment.isMoment(val)) {
					return (val as { format: (fmt: string) => string }).format(dateFormat);
				}
				if (val instanceof Date) {
					return moment ? moment(val).format(dateFormat) : val.toLocaleDateString('fr-FR');
				}
				if (typeof val === 'string') {
					if (moment) {
						const parsed = moment(val, dateFormat, true);
						if (parsed.isValid()) return parsed.format(dateFormat);
					}
					return val;
				}
				return val.toString();
			}

			function formatBackend(val: unknown): string | null {
				if (!val) return null;
				if (moment && moment.isMoment(val)) return (val as { format: (fmt: string) => string }).format(dateFormat);
				if (val instanceof Date) return moment ? moment(val).format(dateFormat) : val.toISOString();
				return val as string;
			}

			function refreshPlaceholder() {
				const s = formatDisplay(filterScope.filter.start);
				const e = formatDisplay(filterScope.filter.end);
				if (s && e) updatePlaceholder(`${s} - ${e}`);
				else if (s) updatePlaceholder(`Dès le ${s}`);
				else if (e) updatePlaceholder(`Jusqu'au ${e}`);
				else updatePlaceholder('Filtrer...');
			}

			// Call initially to render the correct text based on restored state
			refreshPlaceholder();

			filterScope.applyFilter = (e: MouseEvent) => {
				e.stopPropagation();
				closePopup();
				refreshPlaceholder();
				const result: Array<{ type: string; value: unknown }> = [];
				const s = formatBackend(filterScope.filter.start);
				const en = formatBackend(filterScope.filter.end);
				if (s) result.push({ type: 'Ge', value: s });
				if (en) result.push({ type: 'Lt', value: en });
				success(result.length ? result : '');
			};

			filterScope.clearFilter = (e: MouseEvent) => {
				e.stopPropagation();
				filterScope.filter.start = null;
				filterScope.filter.end = null;
				closePopup();
				refreshPlaceholder();
				success('');
			};
		}
	});
}

// ─────────────────────────────────────────────────────────────────────────────
// makeNumberRangeFilter — contenu spécifique uniquement
// ─────────────────────────────────────────────────────────────────────────────

export function makeNumberRangeFilter(_cell: unknown, _onRendered: (cb: () => void) => void, success: (val: unknown) => void) {
	return createPopupFilter({
		triggerIconClass: 'fa fa-filter',
		renderContent(popup, closePopup, updatePlaceholder) {
			popup.innerHTML = `
                <div style="margin-bottom:5px;"><strong>Min</strong></div>
                <input type="number" class="form-control input-sm num-min" placeholder="Val. minimale" style="margin-bottom:10px;" />
                <div style="margin-bottom:5px;"><strong>Max</strong></div>
                <input type="number" class="form-control input-sm num-max" placeholder="Val. maximale" style="margin-bottom:10px;" />
                <div style="display:flex; gap:5px;">
                    <button class="btn btn-default btn-sm btn-apply" style="flex:1;">Filtrer</button>
                    <button class="btn btn-danger btn-sm btn-clear" title="Effacer"><i class="fa fa-times"></i></button>
                </div>
            `;

			const minInput = popup.querySelector('.num-min') as HTMLInputElement;
			const maxInput = popup.querySelector('.num-max') as HTMLInputElement;

			// Restore initial values from the filter cell if available
			const initialValue = (_cell as { getValue?: () => unknown })?.getValue?.() as Array<{ type: string; value: unknown }> | undefined;
			if (Array.isArray(initialValue)) {
				const geFilter = initialValue.find((f) => f.type === 'Ge');
				const ltFilter = initialValue.find((f) => f.type === 'Lt');
				if (geFilter && geFilter.value != null) {
					minInput.value = geFilter.value.toString();
				}
				if (ltFilter && ltFilter.value != null) {
					maxInput.value = ltFilter.value.toString();
				}
			}

			function refreshPlaceholder() {
				const mn = minInput.value;
				const mx = maxInput.value;
				if (mn && mx) updatePlaceholder(`${mn} – ${mx}`);
				else if (mn) updatePlaceholder(`≥ ${mn}`);
				else if (mx) updatePlaceholder(`< ${mx}`);
				else updatePlaceholder('Filtrer...');
			}

			// Call initially to render the correct text based on restored state
			refreshPlaceholder();

			popup.querySelector('.btn-apply')!.addEventListener('click', (e) => {
				e.stopPropagation();
				closePopup();
				refreshPlaceholder();
				const result: Array<{ type: string; value: number }> = [];
				if (minInput.value !== '') result.push({ type: 'Ge', value: parseFloat(minInput.value) });
				if (maxInput.value !== '') result.push({ type: 'Lt', value: parseFloat(maxInput.value) });
				success(result.length ? result : '');
			});

			popup.querySelector('.btn-clear')!.addEventListener('click', (e) => {
				e.stopPropagation();
				minInput.value = '';
				maxInput.value = '';
				closePopup();
				refreshPlaceholder();
				success('');
			});
		}
	});
}

export const MassiaTabulatorFilterPopupHelpers = {
	makeDateRangeFilter,
	makeNumberRangeFilter,
	destroyAllFilterPopups
};
